import React from "react";
import { motion } from "framer-motion";
import { Clock, Calendar } from "lucide-react";
import { useLang } from "../context/LanguageContext";

function currentDayIndex() {
  const d = new Date().getDay();
  return (d + 6) % 7; // Monday = 0
}

export default function Schedule() {
  const { t } = useLang();
  const todayIdx = currentDayIndex();

  return (
    <section id="schedule" data-testid="schedule-section" className="relative bg-black py-24 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-6"
          >
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-[#FFCC00] mb-6">
              / 03 — {t.schedule.overline}
            </div>
            <h2 data-testid="schedule-title" className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.92] mb-6">
              {t.schedule.title}
            </h2>
            <p className="text-base md:text-lg text-white/70 font-body max-w-lg mb-10">
              {t.schedule.desc}
            </p>

            <div className="inline-flex items-center gap-4 px-6 py-5 border border-white/15 bg-[#0a0a0a]" data-testid="schedule-info-badge">
              <Calendar size={22} className="text-[#FFCC00]" />
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">365 / 365</div>
                <div className="font-display text-2xl text-white">
                  05:00 — 01:00
                </div>
              </div>
              <Clock size={22} className="text-white/40 ml-4" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="md:col-span-6"
          >
            <div className="border border-white/10 divide-y divide-white/10 bg-[#080808]" data-testid="schedule-table">
              {t.schedule.days.map((day, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-6 md:px-8 py-5 md:py-6 transition-colors ${
                    i === todayIdx ? "bg-[#FFCC00]/10" : "hover:bg-white/[0.02]"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {i === todayIdx && (
                      <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-black bg-[#FFCC00] px-2 py-1">
                        {t.schedule.today}
                      </span>
                    )}
                    <span className={`font-display text-xl md:text-2xl ${i === todayIdx ? "text-[#FFCC00]" : "text-white"}`}>
                      {day.d}
                    </span>
                  </div>
                  <span className="font-body text-sm md:text-base text-white/70 tracking-wide tabular-nums">
                    {day.h}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
